import type { RealEstateCase, ShotScore, ShotScoreActivityType } from "./types";
import type { ShotScoreDraft } from "./uiTypes";
import { classifyDebriefEvent } from "./debrief.ts";

export type ShotScoreAnalytics = {
  sampleSize: number;
  overall: number;
  identity: number;
  value: number;
  acquisition: number;
  nextStep: number;
};

export const shotScoreValue = (values: Pick<ShotScoreDraft, "identity" | "value" | "acquisition" | "nextStep">) =>
  ([values.identity, values.value, values.acquisition, values.nextStep].filter(Boolean).length * 25) as ShotScore["score"];

export const followUpActivityType = (caseItem: RealEstateCase): ShotScoreActivityType | undefined => {
  if (!caseItem.followUp) return undefined;
  const eventType = classifyDebriefEvent(caseItem, caseItem.followUp);
  if (eventType === "property_viewing") return "viewing";
  return eventType ? "meeting" : undefined;
};

export const aggregateShotScores = (scores: ShotScore[], limit = 30): ShotScoreAnalytics => {
  const sample = [...scores]
    .sort((first, second) => Date.parse(second.completedAt) - Date.parse(first.completedAt))
    .slice(0, limit);
  if (sample.length === 0) {
    return { sampleSize: 0, overall: 0, identity: 0, value: 0, acquisition: 0, nextStep: 0 };
  }

  const percentage = (dimension: keyof Pick<ShotScore, "identity" | "value" | "acquisition" | "nextStep">) =>
    Math.round((sample.filter((score) => score[dimension]).length / sample.length) * 100);

  return {
    sampleSize: sample.length,
    overall: Math.round(sample.reduce((sum, score) => sum + score.score, 0) / sample.length),
    identity: percentage("identity"),
    value: percentage("value"),
    acquisition: percentage("acquisition"),
    nextStep: percentage("nextStep")
  };
};

export const allCaseShotScores = (cases: RealEstateCase[]) => cases.flatMap((caseItem) => caseItem.shotScores ?? []);
